import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { DealContactsPanel } from '../components/DealContacts';
import { DealRetainerSection } from '../components/DealRetainer';
import { PageHeader } from '../components/Layout';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Field,
  Input,
  Loading,
  Modal,
  Select,
  StatusBadge,
  Table,
  Td,
  Textarea,
  Th,
} from '../components/ui';
import {
  DEAL_STAGES,
  OPEN_DEAL_STAGES,
  api,
  isClosedDealStage,
  type Campaign,
  type Deal,
  type DealContact,
  type DealHistoryEntry,
  type DealStage,
  type PipelineStage,
  type Prospect,
  type StageVelocity,
} from '../lib/api';
import { date, dateOnly, daysSince, humanize, moneyIn, percent, relative } from '../lib/format';
import { useAction, useApi, useDebounced } from '../lib/hooks';

export function Pipeline() {
  const [campaignId, setCampaignId] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  /** Set when a stage move would close a deal, so a reason can be captured first. */
  const [closing, setClosing] = useState<{ deal: Deal; stage: DealStage } | null>(null);

  const suffix = campaignId ? `?campaign_id=${campaignId}` : '';
  const pipeline = useApi<{ pipeline: PipelineStage[] }>(`/api/deals/pipeline${suffix}`);
  const velocity = useApi<{ velocity: StageVelocity[] }>(`/api/deals/velocity${suffix}`);
  const campaigns = useApi<{ campaigns: Campaign[] }>('/api/campaigns');

  const dealsPath = useMemo(() => {
    const params = new URLSearchParams({ state: 'open', limit: '500' });
    if (campaignId) params.set('campaign_id', campaignId);
    return `/api/deals?${params.toString()}`;
  }, [campaignId]);
  const deals = useApi<{ deals: Deal[]; total: number; stale_after_days: number }>(dealsPath);

  const columns = pipeline.data?.pipeline ?? [];
  const openDeals = deals.data?.deals ?? [];
  const staleAfterDays = deals.data?.stale_after_days ?? 21;

  function refresh() {
    pipeline.reload();
    deals.reload();
    velocity.reload();
  }

  const move = useAction(async (dealId: string, stage: DealStage, closeReason?: string) => {
    await api.post(`/api/deals/${dealId}/stage`, { stage, close_reason: closeReason });
    refresh();
  });

  /** Closing needs a reason, so it detours through a modal; open moves go straight through. */
  function requestMove(deal: Deal, stage: DealStage) {
    if (stage === deal.stage) return;
    if (isClosedDealStage(stage)) setClosing({ deal, stage });
    else void move.run(deal.id, stage);
  }

  const totalOpen = columns.reduce((sum, column) => sum + column.value, 0);
  const totalCount = columns.reduce((sum, column) => sum + column.count, 0);

  // The API sums value per stage regardless of currency, because deals carry
  // their own. Rather than print a total that silently mixes EUR into USD, the
  // header says so when more than one currency is actually in view.
  const currencies = Array.from(new Set(openDeals.map((deal) => deal.currency)));
  const mixedCurrency = currencies.length > 1;

  return (
    <>
      <PageHeader
        title="Pipeline"
        description={`${totalCount} open ${totalCount === 1 ? 'deal' : 'deals'}`}
        actions={
          <>
            <Select
              value={campaignId}
              onChange={(event) => setCampaignId(event.target.value)}
              className="w-auto"
              aria-label="Filter by campaign"
            >
              <option value="">All campaigns</option>
              {campaigns.data?.campaigns.map((campaign) => (
                <option key={campaign.id} value={campaign.id}>
                  {campaign.name}
                </option>
              ))}
            </Select>
            <Button variant="primary" onClick={() => setShowNew(true)}>
              New deal
            </Button>
          </>
        }
      />

      {(pipeline.error || deals.error) && (
        <div className="mb-4">
          <ErrorNote message={pipeline.error ?? deals.error ?? ''} onRetry={refresh} />
        </div>
      )}
      {move.error && (
        <div className="mb-4">
          <ErrorNote message={move.error} onRetry={move.clearError} />
        </div>
      )}

      <div className="mb-5 grid gap-3 sm:grid-cols-3">
        <div className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-ink-200/70">
          <p className="text-xs font-medium uppercase tracking-wide text-ink-500">Open pipeline</p>
          <p className="mt-1 text-2xl font-semibold tabular-nums text-ink-900">
            {moneyIn(totalOpen, mixedCurrency ? 'USD' : currencies[0])}
          </p>
          <p className="mt-1 text-xs text-ink-500">
            {mixedCurrency
              ? `Mixed currencies (${currencies.join(', ')}) — total is not meaningful`
              : 'Excludes won and lost'}
          </p>
        </div>
        <div className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-ink-200/70">
          <p className="text-xs font-medium uppercase tracking-wide text-ink-500">In negotiation</p>
          <p className="mt-1 text-2xl font-semibold tabular-nums text-ink-900">
            {columns.find((column) => column.stage === 'negotiating')?.count ?? 0}
          </p>
          <p className="mt-1 text-xs text-ink-500">Closest to a decision</p>
        </div>
        <div className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-ink-200/70">
          <p className="text-xs font-medium uppercase tracking-wide text-ink-500">Awaiting a quote answer</p>
          <p className="mt-1 text-2xl font-semibold tabular-nums text-ink-900">
            {columns.find((column) => column.stage === 'quoted')?.count ?? 0}
          </p>
          <p className="mt-1 text-xs text-ink-500">
            {(() => {
              const quoted = columns.find((column) => column.stage === 'quoted');
              return quoted?.median_age_days != null
                ? `${quoted.median_age_days}d median age`
                : 'No quotes outstanding';
            })()}
          </p>
        </div>
      </div>

      {pipeline.loading && !pipeline.data ? (
        <Loading label="Loading pipeline" />
      ) : totalCount === 0 ? (
        <EmptyState
          title="No open deals"
          description="A deal tracks one opportunity through to won or lost. Create one against a prospect to start using the board."
          action={
            <Button variant="primary" onClick={() => setShowNew(true)}>
              New deal
            </Button>
          }
        />
      ) : (
        <div className="overflow-x-auto pb-2">
          <div className="grid min-w-[56rem] grid-cols-4 gap-3">
            {columns.map((column) => (
              <StageColumn
                key={column.stage}
                column={column}
                deals={openDeals.filter((deal) => deal.stage === column.stage)}
                staleAfterDays={staleAfterDays}
                onOpen={setDetailId}
                onMove={requestMove}
                moving={move.pending}
              />
            ))}
          </div>
        </div>
      )}

      {velocity.data && velocity.data.velocity.some((row) => row.transitions > 0) && (
        <Card className="mt-6" title="Stage velocity">
          <p className="mb-3 text-xs text-ink-500">
            Median time deals spent in each stage before leaving it, over completed passes only — a
            deal sitting in a stage right now has no end time and would otherwise make the stage look
            faster the more stuck deals it holds.
          </p>
          <Table>
            <thead>
              <tr>
                <Th>Stage</Th>
                <Th className="text-right">Completed passes</Th>
                <Th className="text-right">Median days</Th>
                <Th className="text-right">Went on to win</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {velocity.data.velocity
                .filter((row) => row.transitions > 0)
                .map((row) => (
                  <tr key={row.stage}>
                    <Td>
                      <StatusBadge status={row.stage} />
                    </Td>
                    <Td className="text-right tabular-nums">{row.transitions}</Td>
                    <Td className="text-right tabular-nums">
                      {row.median_days != null ? `${row.median_days}d` : '—'}
                    </Td>
                    <Td className="text-right tabular-nums">{percent(row.progression_rate)}</Td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </Card>
      )}

      <NewDealModal
        open={showNew}
        onClose={() => setShowNew(false)}
        onCreated={() => {
          setShowNew(false);
          refresh();
        }}
      />

      <CloseDealModal
        pending={move.pending}
        request={closing}
        onClose={() => setClosing(null)}
        onConfirm={async (reason) => {
          if (!closing) return;
          await move.run(closing.deal.id, closing.stage, reason);
          setClosing(null);
        }}
      />

      <DealDetailModal
        dealId={detailId}
        onClose={() => setDetailId(null)}
        onChanged={refresh}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Board
// ---------------------------------------------------------------------------
function StageColumn({
  column,
  deals,
  staleAfterDays,
  onOpen,
  onMove,
  moving,
}: {
  column: PipelineStage;
  deals: Deal[];
  staleAfterDays: number;
  onOpen: (id: string) => void;
  onMove: (deal: Deal, stage: DealStage) => void;
  moving: boolean;
}) {
  return (
    <div className="flex flex-col rounded-lg bg-ink-100/60 ring-1 ring-ink-200/70">
      <header className="border-b border-ink-200/70 px-3 py-2.5">
        <div className="flex items-center justify-between gap-2">
          <StatusBadge status={column.stage} />
          <span className="text-xs font-medium tabular-nums text-ink-500">{column.count}</span>
        </div>
        <p className="mt-1.5 text-sm font-semibold tabular-nums text-ink-900">
          {moneyIn(column.value, deals[0]?.currency)}
        </p>
        {column.median_age_days != null && (
          <p className="text-xs text-ink-500">{column.median_age_days}d median age</p>
        )}
      </header>

      <div className="flex flex-col gap-2 p-2">
        {deals.length === 0 ? (
          <p className="px-1 py-6 text-center text-xs text-ink-400">Empty</p>
        ) : (
          deals.map((deal) => (
            <DealCard key={deal.id} deal={deal} staleAfterDays={staleAfterDays} onOpen={onOpen} onMove={onMove} moving={moving} />
          ))
        )}
      </div>
    </div>
  );
}

function DealCard({
  deal,
  staleAfterDays,
  onOpen,
  onMove,
  moving,
}: {
  deal: Deal;
  staleAfterDays: number;
  onOpen: (id: string) => void;
  onMove: (deal: Deal, stage: DealStage) => void;
  moving: boolean;
}) {
  const age = daysSince(deal.stage_entered_at);
  // Quiet, not merely old: the API measures from the last timeline event on the
  // prospect or company, so a deal in one stage for a month but still being
  // talked about is not flagged. The threshold comes from the server.
  const quietDays = daysSince(deal.quiet_since);
  const stale = quietDays !== null && quietDays >= staleAfterDays;

  return (
    <div className="rounded-md bg-white p-2.5 shadow-sm ring-1 ring-ink-200/70">
      <button onClick={() => onOpen(deal.id)} className="block w-full text-left">
        <p className="truncate text-sm font-medium text-ink-900">
          {deal.name || deal.company_name || 'Untitled deal'}
        </p>
        <p className="truncate text-xs text-ink-500">
          {deal.prospect_name}
          {deal.company_name && deal.name ? ` · ${deal.company_name}` : ''}
        </p>
        <div className="mt-1.5 flex items-center justify-between gap-2">
          <span className="text-sm font-semibold tabular-nums text-ink-900">
            {moneyIn(deal.value, deal.currency)}
          </span>
          {age !== null && (
            <span className="text-xs tabular-nums text-ink-400" title="Time in this stage">
              {age}d
            </span>
          )}
        </div>
        {stale && (
          <div className="mt-1.5">
            <Badge
              tone="amber"
              title={`Nothing on this contact's or company's timeline for ${staleAfterDays}+ days. It is also on Today.`}
            >
              Quiet {quietDays}d
            </Badge>
          </div>
        )}
        {deal.expected_close_date && (
          <p className="mt-0.5 text-xs text-ink-400">Close {dateOnly(deal.expected_close_date)}</p>
        )}
      </button>

      {/* A select rather than drag-and-drop: it is keyboard and screen-reader
          usable, and works on touch without a gesture library. */}
      <Select
        value={deal.stage}
        disabled={moving}
        onChange={(event) => onMove(deal, event.target.value as DealStage)}
        aria-label={`Move ${deal.name || 'deal'} to another stage`}
        className="mt-2 !py-1 text-xs"
      >
        {DEAL_STAGES.map((stage) => (
          <option key={stage} value={stage}>
            {humanize(stage)}
          </option>
        ))}
      </Select>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Close confirmation
// ---------------------------------------------------------------------------
function CloseDealModal({
  request,
  pending,
  onClose,
  onConfirm,
}: {
  request: { deal: Deal; stage: DealStage } | null;
  pending: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');

  if (!request) return null;
  const won = request.stage === 'won';

  return (
    <Modal open onClose={onClose} title={won ? 'Mark deal won' : 'Mark deal lost'}>
      <div className="grid gap-3">
        <p className="text-sm text-ink-600">
          {request.deal.name || request.deal.company_name} ·{' '}
          {moneyIn(request.deal.value, request.deal.currency)}
        </p>
        <Field
          label={won ? 'Why did it land?' : 'Why was it lost?'}
          hint="Feeds the win/loss breakdown in Analytics. Worth a sentence."
        >
          <Textarea
            rows={3}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder={won ? 'Beat incumbent on turnaround' : 'Went with a cheaper vendor'}
          />
        </Field>
        {won && (
          <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800 ring-1 ring-inset ring-amber-200">
            Marking a deal won also marks the prospect won, which takes them out of any automated
            sequence. Their other open deals stay open.
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={pending} onClick={() => onConfirm(reason)}>
            {won ? 'Mark won' : 'Mark lost'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// New deal
// ---------------------------------------------------------------------------
function NewDealModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [search, setSearch] = useState('');
  const [prospectId, setProspectId] = useState('');
  const [name, setName] = useState('');
  const [value, setValue] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [stage, setStage] = useState<DealStage>('lead');
  const [expected, setExpected] = useState('');

  const debounced = useDebounced(search);
  const results = useApi<{ prospects: Prospect[] }>(
    open && debounced ? `/api/prospects?search=${encodeURIComponent(debounced)}&limit=20` : null,
  );

  const create = useAction(async () => {
    await api.post('/api/deals', {
      prospect_id: prospectId,
      name,
      stage,
      value: value ? Number(value) : null,
      currency,
      expected_close_date: expected || null,
    });
    setSearch('');
    setProspectId('');
    setName('');
    setValue('');
    setExpected('');
    setStage('lead');
    onCreated();
  });

  const chosen = results.data?.prospects.find((prospect) => prospect.id === prospectId);

  return (
    <Modal open={open} onClose={onClose} title="New deal">
      <div className="grid gap-3">
        {create.error && <ErrorNote message={create.error} />}

        <Field label="Prospect" hint="Search by name, company or email.">
          <Input
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setProspectId('');
            }}
            placeholder="Meridian Legal"
          />
        </Field>

        {chosen ? (
          <div className="flex items-center justify-between gap-2 rounded-md bg-ink-50 px-3 py-2 text-sm">
            <span className="truncate">
              {chosen.first_name} {chosen.last_name}
              <span className="text-ink-500"> · {chosen.company_name}</span>
            </span>
            <Button size="sm" onClick={() => setProspectId('')}>
              Change
            </Button>
          </div>
        ) : (
          debounced && (
            <div className="max-h-40 overflow-y-auto rounded-md ring-1 ring-ink-200">
              {results.loading ? (
                <p className="px-3 py-2 text-sm text-ink-500">Searching…</p>
              ) : results.data?.prospects.length ? (
                results.data.prospects.map((prospect) => (
                  <button
                    key={prospect.id}
                    onClick={() => setProspectId(prospect.id)}
                    className="block w-full px-3 py-2 text-left text-sm hover:bg-ink-50"
                  >
                    {prospect.first_name} {prospect.last_name}
                    <span className="text-ink-500"> · {prospect.company_name}</span>
                  </button>
                ))
              ) : (
                <p className="px-3 py-2 text-sm text-ink-500">No match</p>
              )}
            </div>
          )
        )}

        <Field label="Deal name" hint="What is being bought — not the company name.">
          <Input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Q3 EN&gt;ES contract set"
          />
        </Field>

        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Value">
            <Input
              type="number"
              min="0"
              value={value}
              onChange={(event) => setValue(event.target.value)}
              placeholder="12000"
            />
          </Field>
          <Field label="Currency">
            <Select value={currency} onChange={(event) => setCurrency(event.target.value)}>
              {['USD', 'EUR', 'GBP', 'CAD', 'CHF'].map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Stage">
            <Select value={stage} onChange={(event) => setStage(event.target.value as DealStage)}>
              {OPEN_DEAL_STAGES.map((option) => (
                <option key={option} value={option}>
                  {humanize(option)}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <Field label="Expected close" hint="Optional.">
          <Input type="date" value={expected} onChange={(event) => setExpected(event.target.value)} />
        </Field>

        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={create.pending}
            disabled={!prospectId}
            onClick={() => void create.run()}
          >
            Create deal
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Detail
// ---------------------------------------------------------------------------
function DealDetailModal({
  dealId,
  onClose,
  onChanged,
}: {
  dealId: string | null;
  onClose: () => void;
  onChanged: () => void;
}) {
  const detail = useApi<{ deal: Deal; history: DealHistoryEntry[]; contacts: DealContact[] }>(
    dealId ? `/api/deals/${dealId}` : null,
  );

  const remove = useAction(async (id: string) => {
    await api.del(`/api/deals/${id}`);
    onChanged();
    onClose();
  });

  if (!dealId) return null;
  const deal = detail.data?.deal;

  return (
    <Modal open onClose={onClose} title={deal?.name || 'Deal'} width="max-w-2xl">
      {detail.loading && !detail.data ? (
        <Loading />
      ) : detail.error ? (
        <ErrorNote message={detail.error} onRetry={detail.reload} />
      ) : deal ? (
        <div className="grid gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={deal.stage} />
            <span className="text-lg font-semibold tabular-nums text-ink-900">
              {moneyIn(deal.value, deal.currency)}
            </span>
            {deal.owner_name && <Badge tone="neutral">{deal.owner_name}</Badge>}
          </div>

          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-3">
            <Detail label="Prospect">
              <Link
                to={`/prospects/${deal.prospect_id}`}
                onClick={onClose}
                className="text-accent-700 underline underline-offset-2"
              >
                {deal.prospect_name || 'View'}
              </Link>
            </Detail>
            <Detail label="Company">{deal.company_name || '—'}</Detail>
            <Detail label="Campaign">{deal.campaign_name || '—'}</Detail>
            <Detail label="In stage since">{relative(deal.stage_entered_at)}</Detail>
            <Detail label="Expected close">{dateOnly(deal.expected_close_date)}</Detail>
            <Detail label="Closed">{deal.closed_at ? date(deal.closed_at) : '—'}</Detail>
          </dl>

          {deal.close_reason && (
            <div className="rounded-md bg-ink-50 px-3 py-2 text-sm text-ink-700">
              <span className="font-medium">Reason: </span>
              {deal.close_reason}
            </div>
          )}

          <DealRetainerSection deal={deal} onChanged={() => { detail.reload(); onChanged(); }} />

          <DealContactsPanel deal={deal} contacts={detail.data?.contacts ?? []} onChanged={detail.reload} />

          <div>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-500">
              Stage history
            </h3>
            <ol className="grid gap-1.5">
              {detail.data?.history.map((entry) => (
                <li key={entry.id} className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="text-xs tabular-nums text-ink-400">{date(entry.changed_at)}</span>
                  {entry.from_stage ? (
                    <>
                      <StatusBadge status={entry.from_stage} />
                      <span className="text-ink-400">→</span>
                    </>
                  ) : (
                    <span className="text-xs text-ink-400">created as</span>
                  )}
                  <StatusBadge status={entry.to_stage} />
                  {entry.note && <span className="text-ink-600">{entry.note}</span>}
                  <span className="text-xs text-ink-400">
                    {entry.changed_by_name || 'system'}
                  </span>
                </li>
              ))}
            </ol>
          </div>

          {remove.error && <ErrorNote message={remove.error} />}

          <div className="flex justify-between gap-2 border-t border-ink-100 pt-3">
            <Button
              variant="danger"
              loading={remove.pending}
              onClick={() => void remove.run(deal.id)}
            >
              Delete deal
            </Button>
            <Button onClick={onClose}>Close</Button>
          </div>
        </div>
      ) : null}
    </Modal>
  );
}

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-ink-500">{label}</dt>
      <dd className="text-ink-800">{children}</dd>
    </div>
  );
}
